import {
  Autocomplete,
  Checkbox,
  FormControlLabel,
  ListItemText,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { APP_STORE_FILTER_GROUPS, PROMPT_FILTER_GROUPS } from '../../constants/promptLabels';
import { selectPrompts, setSelectedLabels } from '../../redux/reducers/promptReducer';
import { AppDispatch } from '../../redux/store';
import { FiltersDisplayMode } from '../../Types/enums';
import { PromptLabelCount } from '../../Types/tags';

const PROMPT_FILTER_GROUPS_WITH_SUB_DISCIPLINE = [
  ...PROMPT_FILTER_GROUPS,
  'Sub-Discipline',
] as const;

const FiltersContainer = styled('div')({
  padding: '0px',
  userSelect: 'none',
});

const TitleRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingRight: '15px',
});

const CheckboxContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: '20px',
  marginTop: '5px',
});

const IndividualCheckboxContainer = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const PromptFilters = ({
  displayMode = FiltersDisplayMode.LIST,
  isAIAppStore,
}: {
  displayMode?: FiltersDisplayMode;
  isAIAppStore?: boolean;
}): JSX.Element => {
  const dispatch = useDispatch<AppDispatch>();
  const { labelOptions, selectedLabels } = useSelector(selectPrompts);

  const activeGroupNames = useMemo<readonly string[]>(
    () => (isAIAppStore ? APP_STORE_FILTER_GROUPS : PROMPT_FILTER_GROUPS_WITH_SUB_DISCIPLINE),
    [isAIAppStore]
  );
  const activeGroupNameSet = useMemo(() => new Set<string>(activeGroupNames), [activeGroupNames]);

  const visibleLabelOptions = useMemo(
    () =>
      labelOptions
        .filter((labelOption) => activeGroupNameSet.has(labelOption.type) && labelOption.count > 0)
        .sort(
          (left, right) =>
            activeGroupNames.indexOf(left.type) - activeGroupNames.indexOf(right.type) ||
            left.label.localeCompare(right.label)
        ),
    [activeGroupNameSet, activeGroupNames, labelOptions]
  );

  const activeTagGroupings = useMemo<Record<string, PromptLabelCount[]>>(
    () =>
      activeGroupNames.reduce<Record<string, PromptLabelCount[]>>((groups, groupName) => {
        groups[groupName] = visibleLabelOptions.filter(
          (labelOption) => labelOption.type === groupName
        );
        return groups;
      }, {}),
    [activeGroupNames, visibleLabelOptions]
  );

  const selectedDropdownOptions = useMemo(
    () =>
      selectedLabels
        .map((selectedLabel) =>
          visibleLabelOptions.find((labelOption) => labelOption.label === selectedLabel)
        )
        .filter((labelOption): labelOption is PromptLabelCount => Boolean(labelOption)),
    [selectedLabels, visibleLabelOptions]
  );

  const handleLabelClick = (event: React.ChangeEvent<HTMLInputElement>, label: string) => {
    const newLabels = event.target.checked
      ? [...selectedLabels, label]
      : selectedLabels.filter((selectedLabel) => selectedLabel !== label);

    dispatch(setSelectedLabels(newLabels));
  };

  const handleLabelSelected = (_: React.SyntheticEvent, newValue: PromptLabelCount[]) => {
    dispatch(setSelectedLabels([...new Set(newValue.map((labelOption) => labelOption.label))]));
  };

  if (displayMode === FiltersDisplayMode.DROPDOWN) {
    return (
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={visibleLabelOptions}
        value={selectedDropdownOptions}
        onChange={handleLabelSelected}
        groupBy={(option) => option.type}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.label === value.label}
        renderOption={(props, option, { selected }) => (
          <li {...props} key={`${option.type}-${option.label}`}>
            <Checkbox checked={selected} />
            <ListItemText primary={option.label} secondary={option.count} />
          </li>
        )}
        renderInput={(params) => <TextField {...params} label="Tags" />}
      />
    );
  }

  return (
    <FiltersContainer id="filter-container">
      <TitleRow>
        <Typography
          variant="h5"
          sx={{
            fontSize: '16px',
            fontWeight: '700',
            marginTop: '10px',
            marginBottom: '10px',
            color: '#F900D3',
          }}
        >
          {isAIAppStore ? 'AI App Store Filters' : 'Prompt Filters'}
        </Typography>
      </TitleRow>
      <div
        style={{
          overflowY: 'auto',
          height: 'calc(100vh - 250px - var(--banner-height, 0px))',
          paddingRight: '20px',
        }}
      >
        {activeGroupNames.map((group, index) => {
          const groupLabels = activeTagGroupings[group] ?? [];
          const total = groupLabels.reduce((acc, labelOption) => acc + labelOption.count, 0);

          return (
            <div key={index}>
              <Typography
                variant="h6"
                sx={{ fontWeight: '500', fontSize: '14px', color: '#0047FF' }}
              >
                {group}
              </Typography>
              <CheckboxContainer>
                {total > 0 ? (
                  groupLabels.map(({ label, count }, index2) => {
                    return count > 0 ? (
                      <IndividualCheckboxContainer
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontWeight: '500',
                          color: '#070154',
                          width: '100%',
                        }}
                        key={index2}
                      >
                        <FormControlLabel
                          key={index2}
                          control={
                            <Checkbox
                              checked={selectedLabels.includes(label)}
                              onChange={(event) => handleLabelClick(event, label)}
                            />
                          }
                          label={label}
                          sx={{
                            userSelect: 'none',
                            span: {
                              fontSize: '12px',
                            },
                          }}
                        />
                        <Typography
                          style={{
                            float: 'right',
                            color: 'grey',
                            fontWeight: '500',
                            display: 'flex',
                          }}
                        >
                          {count}
                        </Typography>
                      </IndividualCheckboxContainer>
                    ) : null;
                  })
                ) : (
                  <Typography
                    key={index}
                    variant="h6"
                    sx={{ fontWeight: '500', fontSize: '14px', color: '#070154' }}
                  >
                    No {group} {isAIAppStore ? 'apps' : 'prompts'}
                  </Typography>
                )}
              </CheckboxContainer>
            </div>
          );
        })}
      </div>
    </FiltersContainer>
  );
};

export default PromptFilters;
